import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { Locale } from 'date-fns';
import {
  ar,
  de,
  enUS,
  es,
  fr,
  hi,
  id as idLocale,
  it,
  ja,
  ko,
  ru,
  vi,
  zhCN,
  zhTW,
} from 'date-fns/locale';

import { normalizeLocale } from '@/i18n/config';

export type CalendarOccurrence = {
  kind: 'actual' | 'projected';
  scheduleId: string;
  runId?: string;
  title: string;
  scheduledFor: number;
  status: string;
  visibility: 'private' | 'workspace';
  recurrenceKind?: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly';
  scheduleStatus?: 'active' | 'paused' | 'completed' | 'archived';
};

export type OccurrenceStatusKey =
  | 'upcoming'
  | 'running'
  | 'waitingBackground'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'needsAttention'
  | 'unknownOutcome'
  | 'canceled'
  | 'paused'
  | 'malformed';

export type OccurrenceStatusVisual = {
  key: OccurrenceStatusKey;
  tone: 'blue' | 'indigo' | 'green' | 'red' | 'amber' | 'orange' | 'gray';
  attention: boolean;
};

export type CalendarRange = {
  start: Date;
  end: Date;
  days: Date[];
};

type CalendarNavigationKey =
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'Home'
  | 'End';

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;
const MINUTE_MS = 60_000;
const TIMEZONE_OFFSET_PROBE_MS = 18 * 60 * MINUTE_MS;
const zonedFormatterCache = new Map<string, Intl.DateTimeFormat>();

export function getScheduledDateLocale(locale: string): Locale {
  switch (normalizeLocale(locale)) {
    case 'ar':
      return ar;
    case 'de':
      return de;
    case 'es':
      return es;
    case 'fr':
      return fr;
    case 'hi':
      return hi;
    case 'id':
      return idLocale;
    case 'it':
      return it;
    case 'ja':
      return ja;
    case 'ko':
      return ko;
    case 'ru':
      return ru;
    case 'vi':
      return vi;
    case 'zh-TW':
      return zhTW;
    case 'zh':
      return zhCN;
    default:
      return enUS;
  }
}

export function getScheduledWeekStartsOn(
  locale: string
): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  const weekStartsOn = getScheduledDateLocale(locale).options?.weekStartsOn;
  return weekStartsOn !== undefined && weekStartsOn >= 0 && weekStartsOn <= 6
    ? weekStartsOn
    : 1;
}

export function getCalendarRange(cursor: Date, locale: string): CalendarRange {
  const weekStartsOn = getScheduledWeekStartsOn(locale);
  const rangeStart = startOfWeek(startOfMonth(cursor), { weekStartsOn });
  const rangeEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn });

  return {
    start: rangeStart,
    end: rangeEnd,
    days: eachDayOfInterval({ start: rangeStart, end: rangeEnd }),
  };
}

export type CalendarMonthOption = { value: number; label: string };

function daysInCalendarMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Localized short month names for a jump-to-month control. Anchored to the
 * given year only because the noon-anchored formatter needs a concrete date;
 * the label itself never depends on which year is passed. */
export function calendarMonthOptions(
  year: number,
  locale: string,
  timezone: string
): CalendarMonthOption[] {
  return Array.from({ length: 12 }, (_, month) => ({
    value: month,
    label: formatNominalCalendarDate(
      new Date(year, month, 1),
      locale,
      timezone,
      {
        month: 'short',
      }
    ),
  }));
}

/** A fixed window of years around the given one for a jump-to-year control. */
export function calendarYearOptions(year: number, span = 5): number[] {
  return Array.from(
    { length: span * 2 + 1 },
    (_, index) => year - span + index
  );
}

/** Preserves the day of month when jumping months/years, clamped to the
 * target month's length (e.g. Jan 31 -> Feb 28) instead of rolling over. */
export function withCalendarMonth(cursor: Date, month: number): Date {
  const year = cursor.getFullYear();
  const day = Math.min(cursor.getDate(), daysInCalendarMonth(year, month));
  return new Date(year, month, day);
}

export function withCalendarYear(cursor: Date, year: number): Date {
  const month = cursor.getMonth();
  const day = Math.min(cursor.getDate(), daysInCalendarMonth(year, month));
  return new Date(year, month, day);
}

function getZonedFormatter(timezone: string): Intl.DateTimeFormat | null {
  const cached = zonedFormatterCache.get(timezone);
  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US-u-nu-latn', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    zonedFormatterCache.set(timezone, formatter);
    return formatter;
  } catch {
    return null;
  }
}

export function zonedDateTimeParts(
  timestamp: number,
  timezone: string
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} | null {
  const formatter = getZonedFormatter(timezone);
  if (!formatter) return null;
  const values = new Map(
    formatter
      .formatToParts(new Date(timestamp))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)])
  );
  const year = values.get('year');
  const month = values.get('month');
  const day = values.get('day');
  const hour = values.get('hour');
  const minute = values.get('minute');
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined
  ) {
    return null;
  }
  return { year, month, day, hour, minute };
}

function twoDigit(value: number): string {
  return String(value).padStart(2, '0');
}

/** Formats a calendar cursor day without interpreting it as an instant. */
export function nominalDateKey(day: Date): string {
  return `${day.getFullYear()}-${twoDigit(day.getMonth() + 1)}-${twoDigit(
    day.getDate()
  )}`;
}

/**
 * Rebase a nominal calendar date only while it is still following the prior
 * timezone's "today". User navigation is preserved when profile settings load.
 */
export function rebaseNominalDateFollowingToday(
  value: Date,
  previousToday: Date,
  nextToday: Date
): Date {
  return nominalDateKey(value) === nominalDateKey(previousToday)
    ? nextToday
    : value;
}

/** Formats a persisted HH:mm wall-clock value without treating it as an instant. */
export function formatLocalWallClock(
  localTime: string,
  locale: string
): string {
  const match = /^(?<hour>[01]\d|2[0-3]):(?<minute>[0-5]\d)$/.exec(localTime);
  if (!match?.groups) return localTime;
  const hour = Number(match.groups.hour);
  const minute = Number(match.groups.minute);
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(Date.UTC(2000, 0, 1, hour, minute));
}

/** Formats an hourly recurrence as its minute within each hour. */
export function formatHourlyMinute(localTime: string): string {
  return `:${localTime.slice(-2)}`;
}

export function dateKeyInTimezone(
  timestamp: number,
  timezone: string
): string | null {
  const parts = zonedDateTimeParts(timestamp, timezone);
  if (!parts) return null;
  return `${parts.year}-${twoDigit(parts.month)}-${twoDigit(parts.day)}`;
}

export function coalesceCalendarOccurrences<TItem extends CalendarOccurrence>(
  items: readonly TItem[]
): TItem[] {
  const actualKeys = new Set(
    items
      .filter((item) => item.kind === 'actual')
      .map((item) => `${item.scheduleId}:${item.scheduledFor}`)
  );
  return items.filter(
    (item) =>
      item.kind === 'actual' ||
      !actualKeys.has(`${item.scheduleId}:${item.scheduledFor}`)
  );
}

export function getDisplayOccurrenceStatus(
  occurrence: Pick<CalendarOccurrence, 'kind' | 'status' | 'scheduleStatus'>
): string {
  return occurrence.kind === 'projected' &&
    occurrence.scheduleStatus === 'paused'
    ? 'paused'
    : occurrence.status;
}

export function groupOccurrencesByDate<TItem extends CalendarOccurrence>(
  items: readonly TItem[],
  timezone: string
): Map<string, TItem[]> {
  const grouped = new Map<string, TItem[]>();
  for (const item of coalesceCalendarOccurrences(items)) {
    const key = dateKeyInTimezone(item.scheduledFor, timezone);
    if (!key) continue;
    const current = grouped.get(key);
    if (current) current.push(item);
    else grouped.set(key, [item]);
  }
  for (const dayItems of grouped.values()) {
    dayItems.sort(
      (left, right) =>
        left.scheduledFor - right.scheduledFor ||
        Number(right.kind === 'actual') - Number(left.kind === 'actual') ||
        left.title.localeCompare(right.title)
    );
  }
  return grouped;
}

export function splitVisibleOccurrences<TItem extends CalendarOccurrence>(
  items: readonly TItem[],
  visibleLimit: number
): { visible: TItem[]; overflow: TItem[] } {
  const ordered = [...items].sort(
    (left, right) =>
      left.scheduledFor - right.scheduledFor ||
      left.title.localeCompare(right.title)
  );
  return {
    visible: ordered.slice(0, Math.max(0, visibleLimit)),
    overflow: ordered.slice(Math.max(0, visibleLimit)),
  };
}

export function getCalendarTargetIndex(
  currentIndex: number,
  key: CalendarNavigationKey,
  totalItems: number,
  columns = 7,
  direction: 'ltr' | 'rtl' = 'ltr'
): number {
  if (totalItems <= 0) return 0;
  const boundedIndex = clampCalendarActiveIndex(currentIndex, totalItems);
  const rowStart = Math.floor(boundedIndex / columns) * columns;
  const rowEnd = Math.min(rowStart + columns - 1, totalItems - 1);
  switch (key) {
    case 'ArrowLeft':
      return direction === 'rtl'
        ? Math.min(totalItems - 1, boundedIndex + 1)
        : Math.max(0, boundedIndex - 1);
    case 'ArrowRight':
      return direction === 'rtl'
        ? Math.max(0, boundedIndex - 1)
        : Math.min(totalItems - 1, boundedIndex + 1);
    case 'ArrowUp':
      return Math.max(0, boundedIndex - columns);
    case 'ArrowDown':
      return Math.min(totalItems - 1, boundedIndex + columns);
    case 'Home':
      return rowStart;
    case 'End':
      return rowEnd;
  }
}

export function clampCalendarActiveIndex(
  currentIndex: number,
  totalItems: number
): number {
  if (totalItems <= 0) return 0;
  return Math.min(Math.max(0, currentIndex), totalItems - 1);
}

export function getOccurrenceStatusVisual(
  status: string
): OccurrenceStatusVisual {
  switch (status) {
    case 'projected':
    case 'scheduled':
      return { key: 'upcoming', tone: 'blue', attention: false };
    case 'claimed':
    case 'dispatching':
    case 'running':
      return { key: 'running', tone: 'indigo', attention: false };
    case 'waiting_background':
      return { key: 'waitingBackground', tone: 'indigo', attention: false };
    case 'succeeded':
      return { key: 'succeeded', tone: 'green', attention: false };
    case 'failed':
      return { key: 'failed', tone: 'red', attention: false };
    case 'skipped':
      return { key: 'skipped', tone: 'amber', attention: false };
    case 'needs_attention':
      return { key: 'needsAttention', tone: 'orange', attention: true };
    case 'unknown_outcome':
      return { key: 'unknownOutcome', tone: 'orange', attention: true };
    case 'canceled':
      return { key: 'canceled', tone: 'gray', attention: false };
    case 'paused':
      return { key: 'paused', tone: 'gray', attention: false };
    default:
      return { key: 'malformed', tone: 'gray', attention: false };
  }
}

function parseLocalDateAndTime(
  localDate: string,
  localTime: string
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} | null {
  const dateMatch = DATE_PATTERN.exec(localDate);
  const timeMatch = TIME_PATTERN.exec(localTime);
  if (!dateMatch || !timeMatch) return null;
  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  if (hour > 23 || minute > 59) return null;
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day, hour, minute };
}

function wallClockOrdinal(parts: {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}): number {
  return (
    (((parts.year * 13 + parts.month) * 32 + parts.day) * 24 + parts.hour) *
      60 +
    parts.minute
  );
}

/**
 * Presentation-only local-time conversion for calendar previews and tests.
 * The editor must persist only the canonical timestamp returned by the Convex
 * `resolveOneOffLocalTime` API, which remains the recurrence authority.
 */
export function zonedLocalDateTimeToEpoch(
  localDate: string,
  localTime: string,
  timezone: string
): number | null {
  const requested = parseLocalDateAndTime(localDate, localTime);
  if (!requested || !getZonedFormatter(timezone)) return null;
  const approximate = Date.UTC(
    requested.year,
    requested.month - 1,
    requested.day,
    requested.hour,
    requested.minute
  );
  const requestedOrdinal = wallClockOrdinal(requested);
  const offsets = new Set<number>();
  for (const probe of [
    approximate - TIMEZONE_OFFSET_PROBE_MS,
    approximate,
    approximate + TIMEZONE_OFFSET_PROBE_MS,
  ]) {
    const parts = zonedDateTimeParts(probe, timezone);
    if (!parts) return null;
    offsets.add(
      Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute
      ) - probe
    );
  }

  const candidates = [...offsets]
    .map((offset) => approximate - offset)
    .sort((left, right) => left - right);
  const resolved = candidates
    .map((instant) => ({
      instant,
      parts: zonedDateTimeParts(instant, timezone),
    }))
    .filter(
      (
        candidate
      ): candidate is {
        instant: number;
        parts: NonNullable<ReturnType<typeof zonedDateTimeParts>>;
      } => candidate.parts !== null
    );
  const exact = resolved.find(
    ({ parts }) => wallClockOrdinal(parts) === requestedOrdinal
  );
  if (exact) return exact.instant;

  const before = resolved
    .filter(({ parts }) => wallClockOrdinal(parts) < requestedOrdinal)
    .at(-1);
  const after = resolved.find(
    ({ parts }) => wallClockOrdinal(parts) > requestedOrdinal
  );
  if (!before || !after) return null;

  let low = before.instant;
  let high = after.instant;
  while (high - low > MINUTE_MS) {
    const midpoint =
      Math.floor((low + (high - low) / 2) / MINUTE_MS) * MINUTE_MS;
    const parts = zonedDateTimeParts(midpoint, timezone);
    if (!parts) return null;
    if (wallClockOrdinal(parts) > requestedOrdinal) high = midpoint;
    else low = midpoint;
  }
  return high;
}

export function formatNominalCalendarDate(
  day: Date,
  locale: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions
): string {
  const dateKey = nominalDateKey(day);
  const instant = zonedLocalDateTimeToEpoch(dateKey, '12:00', timezone);
  const fallback = Date.UTC(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    12
  );
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: instant === null ? 'UTC' : timezone,
  }).format(instant ?? fallback);
}
